export type TopicKey = 'question' | 'broken' | 'account' | 'recording' | 'feedback' | 'research';

export interface Topic {
  key: TopicKey;
  label: string;
  /** One line under the label on the card. */
  blurb: string;
  /** Prefix for the email subject, so the inbox can be triaged at a glance. */
  subject: string;
  /** What to put in the message box — the details that make a first reply useful. */
  prompt: string;
}

export const TOPICS: Topic[] = [
  {
    key: 'question',
    label: 'A question',
    blurb: 'How something works',
    subject: 'Question',
    prompt: 'What would you like to know? If you were partway through something, say which step.',
  },
  {
    key: 'broken',
    label: "Something's not working",
    blurb: 'An error, or a step that fails',
    subject: 'Something is not working',
    prompt: 'What were you trying to do, what happened instead, and did you see an error message?',
  },
  {
    key: 'account',
    label: 'Sign-in or account',
    blurb: 'Codes, missing animals or recordings',
    subject: 'Sign-in or account',
    prompt:
      'Which email address did you sign in with — and if you used a different one before, that one too?',
  },
  {
    key: 'recording',
    label: 'A recording I submitted',
    blurb: 'A correction or a withdrawal',
    subject: 'A submitted recording',
    prompt: 'The date of the recording and which animal it was of, and what should change.',
  },
  {
    key: 'feedback',
    label: 'Feedback or an idea',
    blurb: 'A confusing step, a missing species',
    subject: 'Feedback',
    prompt: 'Anything that made contributing harder than it should be. Short is fine.',
  },
  {
    key: 'research',
    label: 'Research or commercial use',
    blurb: 'Using the corpus',
    subject: 'Research or commercial use',
    prompt: 'Who you are, what you would like to do with the corpus, and roughly when.',
  },
];

export interface DetailLine {
  label: string;
  value: string;
}

export interface Email {
  subject: string;
  body: string;
}

/**
 * The email exactly as it will be sent — the page previews `details` and the
 * copy button copies this, so what the person sees is what arrives.
 */
export function buildEmail(
  brand: string,
  topic: Topic,
  message: string,
  details: DetailLine[] | null,
): Email {
  const parts = [message.trim()];

  if (details && details.length > 0) {
    parts.push(
      [
        '---',
        'Technical details (added by the app — delete this if you would rather not send it)',
        ...details.map((d) => `${d.label}: ${d.value}`),
      ].join('\n'),
    );
  }

  return { subject: `${brand}: ${topic.subject}`, body: parts.join('\n\n') };
}

export function mailtoHref(address: string, email: Email): string {
  // CRLF is what RFC 6068 asks of a mailto body; some clients collapse bare LF.
  const body = email.body.replace(/\n/g, '\r\n');
  return `mailto:${address}?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(body)}`;
}
